import { useState, useMemo, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  calculateViability,
  type ViabilityResults,
  type ViabilityInputs,
} from "@/lib/viability-calculation";
import { INDUSTRY_LIST } from "@/lib/industry-cpc-database";
import { requestViabilityReport } from "@/lib/viability.functions";

type Locale = "de" | "en";

const RATE_LIMIT_KEY_PREFIX = "calvarius_calculator_count_";
const RATE_LIMIT_MAX = 5;

const COPY = {
  de: {
    aov: "Durchschnittlicher Auftragswert",
    aovTip:
      "Brutto-Bestellwert pro Konversion. Inkl. MwSt. Bei wiederkehrenden Kunden Customer Lifetime Value einsetzen.",
    grossMargin: "Bruttomarge",
    grossMarginTip:
      "Marge nach Wareneinsatz und direkten Produkt-Kosten. NICHT Marketing-Kosten abziehen — das macht der Rechner.",
    marketingMargin: "Marketing-Budget vom Auftragswert",
    marketingMarginTip:
      "Wie viel Prozent des Auftragswerts dürfen Marketing und Vertrieb maximal kosten? Typisch 5-20%. Über 25% wird knapp.",
    cvr: "Aktuelle Conversion-Rate",
    cvrTip:
      "Anteil der Besucher mit Konversion. Falls unbekannt: E-Commerce 1-3%, B2B-Lead-Generation 2-5%.",
    industry: "Branche",
    industryPlaceholder: "Bitte Branche wählen",
    methodNote:
      "Was dieser Rechner methodisch unterscheidet — Standard-Performance-Marketing-Rechner kalkulieren nur die wirtschaftlich mögliche CPC-Schwelle. Calvarius gleicht diese gegen reale Branchen-CPC-Bandbreiten ab und gibt eine ehrliche Einordnung — auch wenn das Ergebnis lautet: in Ihrer Konstellation rechnet sich Performance-Marketing aktuell nicht.",
    submit: "Wirtschaftlichkeit prüfen",
    submitting: "Berechne …",
    again: "Neue Berechnung",
    quota: "Fünf Berechnungen pro Browser in 24 Stunden.",
    quotaHit: "Limit erreicht: maximal fünf Berechnungen pro 24 Stunden.",
    invalidMargin:
      "Marketing-Marge muss kleiner als Bruttomarge sein — sonst negativer Deckungsbeitrag.",
    invalidIndustry: "Bitte eine Branche auswählen.",
    invalidGeneric: "Bitte alle Felder gültig ausfüllen.",
    verdictSolid: "Wirtschaftlich solide",
    verdictTight: "Knapp wirtschaftlich",
    verdictProblematic: "Wirtschaftlich problematisch",
    resultStampLabel: "Berechnete Ergebnisse",
    tableTitle: "Schwellenwerte",
    tableHeaders: ["Metrik", "Wert", "Bedeutung"],
    rowMaxCPA: "Maximal tragfähiger CPA",
    rowMaxCPC: "Maximal tragfähiger CPC",
    rowBreakeven: "Breakeven-Conversion-Rate",
    rowMaxCPAMeaning: "Was eine Konversion maximal kosten darf",
    rowMaxCPCMeaning: "Was ein Klick maximal kosten darf bei aktueller CVR",
    rowBreakevenMeaning:
      "Welche CVR Sie bei Branchen-Median-CPC mindestens brauchen",
    visualTitle: "Wo Sie im Branchen-Vergleich liegen",
    visualLegendMedian: "Branchen-Median",
    visualLegendYours: "Ihr Max-CPC",
    visualLegendRange: "Branchen-Bandbreite (25.–75. Perzentil)",
    reasoningTitle: "Methodische Einordnung",
    cta1Headline: "Detaillierte Performance-Marketing-Analyse per E-Mail bekommen",
    cta1Sub:
      "Wir senden Ihnen eine ausführliche schriftliche Einordnung Ihrer Berechnung plus drei konkrete methodische Empfehlungen für Ihre Konstellation. Keine weitere Kontaktaufnahme, falls Sie das nicht wünschen.",
    cta1Email: "Ihre E-Mail-Adresse",
    cta1Submit: "Analyse anfordern",
    cta1Sending: "Sende …",
    cta1Done:
      "Vielen Dank — die Analyse wird in Kürze versendet. Falls Sie weitere Fragen haben, antworten Sie einfach auf die E-Mail.",
    cta1Error: "Versand fehlgeschlagen. Bitte später erneut versuchen.",
    cta2Headline:
      "Performance-Marketing-Strategiegespräch anfragen — 30 Minuten, kostenfrei",
    cta2Sub:
      "Methodisches Erstgespräch mit konkretem Bezug auf Ihre Wirtschaftlichkeits-Parameter. Kein generischer Sales-Pitch, sondern echte methodische Substanz. Pro Woche begrenzte Termin-Verfügbarkeit.",
    cta2Button: "Strategiegespräch anfragen",
    cta3Headline: "Direkt Performance-Marketing-Mandat anfragen",
    cta3Sub:
      "Sie wissen, dass Sie methodisches Performance-Marketing brauchen? Direkter Einstieg ins Mandat.",
    cta3Button: "Kontakt aufnehmen",
    crossLinkPre:
      "Mehr methodische Substanz zu Performance-Marketing-Pacing finden Sie in unserem Pillar-Beitrag ",
    crossLinkLabel: "Google Ads Budget Pacing Juni 2026",
    crossLinkPost: ".",
    crossLinkTo: "/blog/google-ads-budget-pacing-juni-2026",
    contactTo: "/kontakt",
    subjectParam: "strategiegespraech",
  },
  en: {
    aov: "Average order value",
    aovTip:
      "Gross order value per conversion. Including VAT. For recurring customers, use Customer Lifetime Value.",
    grossMargin: "Gross margin",
    grossMarginTip:
      "Margin after cost of goods and direct product costs. Do NOT subtract marketing costs — calculator does that.",
    marketingMargin: "Marketing budget from order value",
    marketingMarginTip:
      "How many percent of the order value can marketing and sales cost at maximum? Typically 5-20%. Above 25% gets tight.",
    cvr: "Current conversion rate",
    cvrTip:
      "Share of visitors with conversion. If unknown: e-commerce 1-3%, B2B lead generation 2-5%.",
    industry: "Industry",
    industryPlaceholder: "Please select industry",
    methodNote:
      "What methodically distinguishes this calculator — standard performance marketing calculators only calculate the economically possible CPC threshold. Calvarius compares this against real industry CPC ranges and provides an honest assessment — even if the result is: performance marketing does not currently make economic sense in your configuration.",
    submit: "Check viability",
    submitting: "Calculating …",
    again: "New calculation",
    quota: "Five calculations per browser per 24 hours.",
    quotaHit: "Limit reached: max. five calculations per 24 hours.",
    invalidMargin:
      "Marketing margin must be smaller than gross margin — otherwise negative contribution margin.",
    invalidIndustry: "Please select an industry.",
    invalidGeneric: "Please fill in all fields correctly.",
    verdictSolid: "Economically solid",
    verdictTight: "Economically tight",
    verdictProblematic: "Economically problematic",
    resultStampLabel: "Calculated results",
    tableTitle: "Thresholds",
    tableHeaders: ["Metric", "Value", "Meaning"],
    rowMaxCPA: "Maximum sustainable CPA",
    rowMaxCPC: "Maximum sustainable CPC",
    rowBreakeven: "Breakeven conversion rate",
    rowMaxCPAMeaning: "What a conversion can cost at maximum",
    rowMaxCPCMeaning: "What a click can cost at maximum at current CVR",
    rowBreakevenMeaning: "Minimum CVR needed at industry median CPC",
    visualTitle: "Where you stand in industry comparison",
    visualLegendMedian: "Industry median",
    visualLegendYours: "Your max CPC",
    visualLegendRange: "Industry range (25th–75th percentile)",
    reasoningTitle: "Methodical assessment",
    cta1Headline: "Get detailed performance marketing analysis by email",
    cta1Sub:
      "We will send you a detailed written assessment of your calculation plus three concrete methodical recommendations for your configuration. No further contact if you do not wish.",
    cta1Email: "Your email address",
    cta1Submit: "Request analysis",
    cta1Sending: "Sending …",
    cta1Done:
      "Thank you — the analysis will be sent shortly. If you have further questions, simply reply to the email.",
    cta1Error: "Sending failed. Please try again later.",
    cta2Headline:
      "Request performance marketing strategy meeting — 30 minutes, free",
    cta2Sub:
      "Methodical initial conversation with concrete reference to your viability parameters. No generic sales pitch, but real methodical substance. Limited appointment availability per week.",
    cta2Button: "Request strategy meeting",
    cta3Headline: "Request performance marketing mandate directly",
    cta3Sub:
      "You know you need methodical performance marketing? Direct entry into the mandate.",
    cta3Button: "Get in touch",
    crossLinkPre:
      "More methodical substance on performance marketing pacing can be found in our pillar article ",
    crossLinkLabel: "Google Ads Budget Pacing June 2026",
    crossLinkPost: ".",
    crossLinkTo: "/en/blog/google-ads-budget-pacing-june-2026",
    contactTo: "/en/contact",
    subjectParam: "strategy-meeting",
  },
} as const;

function todayKey(): string {
  const d = new Date();
  return `${RATE_LIMIT_KEY_PREFIX}${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function readCount(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(todayKey());
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function incrementCount() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(todayKey(), String(readCount() + 1));
  } catch {
    /* ignore */
  }
}

export function ViabilityCalculator({ locale }: { locale: Locale }) {
  const c = COPY[locale];
  const reportFn = useServerFn(requestViabilityReport);

  const [aov, setAov] = useState("");
  const [grossMargin, setGrossMargin] = useState("");
  const [marketingMargin, setMarketingMargin] = useState("");
  const [conversionRate, setConversionRate] = useState("");
  const [industrySlug, setIndustrySlug] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ViabilityResults | null>(null);
  const [usageId, setUsageId] = useState<number | null>(null);

  const industryOptions = useMemo(
    () =>
      [...INDUSTRY_LIST].sort((a, b) =>
        (locale === "de" ? a.labelDe : a.labelEn).localeCompare(
          locale === "de" ? b.labelDe : b.labelEn,
          locale,
        ),
      ),
    [locale],
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (honeypot.trim() !== "") return;

    const aovN = parseFloat(aov);
    const gmN = parseFloat(grossMargin);
    const mmN = parseFloat(marketingMargin);
    const cvrN = parseFloat(conversionRate);

    if (
      !Number.isFinite(aovN) ||
      !Number.isFinite(gmN) ||
      !Number.isFinite(mmN) ||
      !Number.isFinite(cvrN) ||
      aovN < 1 ||
      aovN > 100000 ||
      gmN < 1 ||
      gmN > 100 ||
      mmN < 0 ||
      mmN > 99 ||
      cvrN < 0.01 ||
      cvrN > 100
    ) {
      setError(c.invalidGeneric);
      return;
    }
    if (mmN >= gmN) {
      setError(c.invalidMargin);
      return;
    }
    if (!industrySlug) {
      setError(c.invalidIndustry);
      return;
    }

    const count = readCount();
    if (count >= RATE_LIMIT_MAX) {
      setError(c.quotaHit);
      return;
    }

    const inputs: ViabilityInputs = {
      aov: aovN,
      grossMargin: gmN,
      marketingMargin: mmN,
      conversionRate: cvrN,
      industrySlug,
    };

    let computed: ViabilityResults;
    try {
      computed = calculateViability(inputs);
    } catch {
      setError(c.invalidGeneric);
      return;
    }

    incrementCount();
    setResults(computed);

    // Track usage (non-blocking) — uses SECURITY DEFINER RPC so the public
    // calculator does not need a broad SELECT policy on tool_usage.
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error: rpcErr } = await supabase.rpc("log_viability_usage", {
        _data: {
          industry: inputs.industrySlug,
          industry_label:
            locale === "de"
              ? computed.industry.labelDe
              : computed.industry.labelEn,
          aov: inputs.aov,
          gross_margin: inputs.grossMargin,
          marketing_margin: inputs.marketingMargin,
          conversion_rate: inputs.conversionRate,
          max_cpa: computed.maxCPA,
          max_cpc: computed.maxCPC,
          industry_median_cpc: computed.industry.medianCPC,
          verdict: computed.verdict,
          cpc_gap_to_median_pct: computed.cpcGapToMedianPct,
          locale,
        },
      });
      if (!rpcErr && typeof data === "number") setUsageId(data);
    } catch (err) {
      console.error("Tool-Tracking fehlgeschlagen:", err);
    }

    // scroll to result
    setTimeout(() => {
      document
        .getElementById("calc-result")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  function reset() {
    setResults(null);
    setError(null);
    setUsageId(null);
  }

  return (
    <div className="mx-auto max-w-3xl">
      {!results && (
        <CalculatorForm
          locale={locale}
          c={c}
          aov={aov}
          setAov={setAov}
          grossMargin={grossMargin}
          setGrossMargin={setGrossMargin}
          marketingMargin={marketingMargin}
          setMarketingMargin={setMarketingMargin}
          conversionRate={conversionRate}
          setConversionRate={setConversionRate}
          industrySlug={industrySlug}
          setIndustrySlug={setIndustrySlug}
          industryOptions={industryOptions}
          honeypot={honeypot}
          setHoneypot={setHoneypot}
          onSubmit={onSubmit}
          error={error}
        />
      )}

      {results && (
        <div id="calc-result" className="space-y-10">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-4">
              {c.resultStampLabel}
            </div>
            <button
              type="button"
              onClick={reset}
              className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline"
            >
              {c.again} →
            </button>
          </div>

          <VerdictBanner locale={locale} c={c} results={results} />
          <ThresholdsTable c={c} results={results} />
          <IndustryComparison locale={locale} c={c} results={results} />
          <VerdictReasoning locale={locale} results={results} c={c} />

          <EmailReportCTA
            locale={locale}
            c={c}
            results={results}
            usageId={usageId}
            reportFn={reportFn}
            inputs={{
              aov: parseFloat(aov),
              grossMargin: parseFloat(grossMargin),
              marketingMargin: parseFloat(marketingMargin),
              conversionRate: parseFloat(conversionRate),
              industrySlug,
            }}
          />

          <StrategyMeetingCTA
            locale={locale}
            c={c}
            results={results}
            inputs={{
              aov: parseFloat(aov),
              grossMargin: parseFloat(grossMargin),
              marketingMargin: parseFloat(marketingMargin),
              conversionRate: parseFloat(conversionRate),
              industrySlug,
            }}
          />

          <DirectMandateCTA c={c} />

          <p className="border-t border-rule pt-8 text-sm leading-relaxed text-ink-3">
            {c.crossLinkPre}
            <Link
              to={c.crossLinkTo}
              className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep"
            >
              {c.crossLinkLabel}
            </Link>
            {c.crossLinkPost}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------- Sub-components ----------------

type CopyT = (typeof COPY)[Locale];

function CalculatorForm(props: {
  locale: Locale;
  c: CopyT;
  aov: string;
  setAov: (v: string) => void;
  grossMargin: string;
  setGrossMargin: (v: string) => void;
  marketingMargin: string;
  setMarketingMargin: (v: string) => void;
  conversionRate: string;
  setConversionRate: (v: string) => void;
  industrySlug: string;
  setIndustrySlug: (v: string) => void;
  industryOptions: typeof INDUSTRY_LIST;
  honeypot: string;
  setHoneypot: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  error: string | null;
}) {
  const { locale, c } = props;
  const labelCls =
    "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3";
  const inputCls =
    "w-full rounded-md border border-rule bg-white px-4 py-3 text-ink outline-none transition focus:border-cyan-deep focus:ring-2 focus:ring-cyan-deep/20";

  return (
    <form
      onSubmit={props.onSubmit}
      noValidate
      className="rounded-lg border border-rule bg-white p-6 md:p-8"
    >
      <div className="grid gap-5 md:grid-cols-2">
        <NumberField
          id="aov"
          label={c.aov}
          tooltip={c.aovTip}
          suffix="€"
          placeholder="250"
          value={props.aov}
          onChange={props.setAov}
          min={1}
          max={100000}
          step="0.01"
          labelCls={labelCls}
          inputCls={inputCls}
        />
        <NumberField
          id="gm"
          label={c.grossMargin}
          tooltip={c.grossMarginTip}
          suffix="%"
          placeholder="35"
          value={props.grossMargin}
          onChange={props.setGrossMargin}
          min={1}
          max={100}
          step="0.01"
          labelCls={labelCls}
          inputCls={inputCls}
        />
        <NumberField
          id="mm"
          label={c.marketingMargin}
          tooltip={c.marketingMarginTip}
          suffix="%"
          placeholder="15"
          value={props.marketingMargin}
          onChange={props.setMarketingMargin}
          min={0}
          max={99}
          step="0.01"
          labelCls={labelCls}
          inputCls={inputCls}
        />
        <NumberField
          id="cvr"
          label={c.cvr}
          tooltip={c.cvrTip}
          suffix="%"
          placeholder="2.5"
          value={props.conversionRate}
          onChange={props.setConversionRate}
          min={0.01}
          max={100}
          step="0.01"
          labelCls={labelCls}
          inputCls={inputCls}
        />
      </div>

      <div className="mt-5">
        <label htmlFor="industry" className={labelCls}>
          {c.industry}
        </label>
        <select
          id="industry"
          className={inputCls}
          value={props.industrySlug}
          onChange={(e) => props.setIndustrySlug(e.target.value)}
          required
        >
          <option value="">{c.industryPlaceholder}</option>
          {props.industryOptions.map((ind) => (
            <option key={ind.slug} value={ind.slug}>
              {locale === "de" ? ind.labelDe : ind.labelEn}
            </option>
          ))}
        </select>
      </div>

      {/* Honeypot */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        <label htmlFor="vc-website">Website</label>
        <input
          id="vc-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={props.honeypot}
          onChange={(e) => props.setHoneypot(e.target.value)}
        />
      </div>

      {/* Method note */}
      <div className="mt-7 rounded-md border-l-2 border-cyan/60 bg-bg-subtle px-5 py-4 text-sm leading-relaxed text-ink-2">
        {c.methodNote}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90"
        >
          {c.submit} →
        </button>
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-4">
          {c.quota}
        </span>
      </div>

      {props.error && (
        <p
          role="alert"
          className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {props.error}
        </p>
      )}
    </form>
  );
}

function NumberField(props: {
  id: string;
  label: string;
  tooltip: string;
  suffix: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  min: number;
  max: number;
  step: string;
  labelCls: string;
  inputCls: string;
}) {
  return (
    <div>
      <label htmlFor={props.id} className={props.labelCls}>
        {props.label}
        <span
          className="ml-1.5 inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-ink-4 text-[9px] text-ink-4"
          title={props.tooltip}
          aria-label={props.tooltip}
        >
          ?
        </span>
      </label>
      <div className="relative">
        <input
          id={props.id}
          type="number"
          inputMode="decimal"
          placeholder={props.placeholder}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          min={props.min}
          max={props.max}
          step={props.step}
          required
          className={`${props.inputCls} pr-10`}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-sm text-ink-4">
          {props.suffix}
        </span>
      </div>
    </div>
  );
}

function VerdictBanner({
  locale: _locale,
  c,
  results,
}: {
  locale: Locale;
  c: CopyT;
  results: ViabilityResults;
}) {
  const v = results.verdict;
  const label =
    v === "solid"
      ? c.verdictSolid
      : v === "tight"
        ? c.verdictTight
        : c.verdictProblematic;

  const tone =
    v === "solid"
      ? {
          bg: "rgba(192,232,128,0.18)",
          border: "rgba(120,180,60,0.85)",
          text: "#1f3d0a",
        }
      : v === "tight"
        ? {
            bg: "rgba(255,165,80,0.15)",
            border: "rgba(217,119,6,0.85)",
            text: "#7a3e0a",
          }
        : {
            bg: "rgba(255,90,90,0.12)",
            border: "rgba(220,38,38,0.85)",
            text: "#7a1010",
          };

  const gapSign = results.cpcGapToMedianPct >= 0 ? "+" : "";
  const sub =
    v === "solid"
      ? `Max CPC ${results.maxCPC.toFixed(2)} € · ${gapSign}${results.cpcGapToMedianPct.toFixed(1)}% ${_locale === "de" ? "über" : "above"} ${_locale === "de" ? "Branchen-Median" : "industry median"} (${results.industry.medianCPC.toFixed(2)} €)`
      : v === "tight"
        ? `Max CPC ${results.maxCPC.toFixed(2)} € · ${gapSign}${results.cpcGapToMedianPct.toFixed(1)}% ${_locale === "de" ? "zum Branchen-Median" : "vs. industry median"} (${results.industry.medianCPC.toFixed(2)} €)`
        : `Max CPC ${results.maxCPC.toFixed(2)} € · ${results.cpcGapToMedianPct.toFixed(1)}% ${_locale === "de" ? "unter Branchen-Median" : "below industry median"} (${results.industry.medianCPC.toFixed(2)} €)`;

  return (
    <div
      className="rounded-lg border-l-[4px] px-6 py-6 md:px-8 md:py-7"
      style={{
        background: tone.bg,
        borderLeftColor: tone.border,
        borderTop: `1px solid ${tone.border}`,
        borderRight: `1px solid ${tone.border}`,
        borderBottom: `1px solid ${tone.border}`,
      }}
    >
      <div
        className="font-display text-2xl md:text-3xl"
        style={{ color: tone.text }}
      >
        {label}
      </div>
      <div
        className="mt-3 font-mono text-[13px] tracking-wide"
        style={{ color: tone.text, opacity: 0.85 }}
      >
        {sub}
      </div>
    </div>
  );
}

function ThresholdsTable({
  c,
  results,
}: {
  c: CopyT;
  results: ViabilityResults;
}) {
  return (
    <div>
      <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
        {c.tableTitle}
      </h3>
      <div className="overflow-hidden rounded-lg border border-rule bg-white">
        <table className="w-full text-sm">
          <thead className="bg-bg-subtle">
            <tr className="text-left">
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-3">
                {c.tableHeaders[0]}
              </th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-3">
                {c.tableHeaders[1]}
              </th>
              <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-3">
                {c.tableHeaders[2]}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-rule">
              <td className="px-4 py-3 text-ink">{c.rowMaxCPA}</td>
              <td className="px-4 py-3 font-mono text-ink">
                {results.maxCPA.toFixed(2)} €
              </td>
              <td className="px-4 py-3 text-ink-3">{c.rowMaxCPAMeaning}</td>
            </tr>
            <tr className="border-t border-rule">
              <td className="px-4 py-3 text-ink">{c.rowMaxCPC}</td>
              <td className="px-4 py-3 font-mono text-ink">
                {results.maxCPC.toFixed(2)} €
              </td>
              <td className="px-4 py-3 text-ink-3">{c.rowMaxCPCMeaning}</td>
            </tr>
            <tr className="border-t border-rule">
              <td className="px-4 py-3 text-ink">{c.rowBreakeven}</td>
              <td className="px-4 py-3 font-mono text-ink">
                {results.breakevenConversionRate.toFixed(2)} %
              </td>
              <td className="px-4 py-3 text-ink-3">{c.rowBreakevenMeaning}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IndustryComparison({
  locale,
  c,
  results,
}: {
  locale: Locale;
  c: CopyT;
  results: ViabilityResults;
}) {
  const ind = results.industry;
  // Scale: 0 to max( ind.maxCPC, results.maxCPC ) * 1.1
  const scaleMax = Math.max(ind.maxCPC, results.maxCPC, ind.medianCPC) * 1.15;
  const pct = (n: number) => Math.max(0, Math.min(100, (n / scaleMax) * 100));

  const minP = pct(ind.minCPC);
  const maxP = pct(ind.maxCPC);
  const medianP = pct(ind.medianCPC);
  const yoursP = pct(results.maxCPC);

  const yoursColor =
    results.verdict === "solid"
      ? "#4d8a1a"
      : results.verdict === "tight"
        ? "#d97706"
        : "#dc2626";

  return (
    <div>
      <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
        {c.visualTitle}
      </h3>
      <div className="rounded-lg border border-rule bg-white p-6">
        <svg
          viewBox="0 0 100 22"
          preserveAspectRatio="none"
          className="h-24 w-full"
          role="img"
          aria-label={c.visualTitle}
        >
          {/* baseline */}
          <line
            x1="0"
            y1="14"
            x2="100"
            y2="14"
            stroke="#e5e7eb"
            strokeWidth="0.15"
          />
          {/* range bar 25–75 percentile */}
          <rect
            x={minP}
            y="11"
            width={Math.max(0.1, maxP - minP)}
            height="6"
            fill="rgba(10,106,191,0.18)"
            stroke="rgba(10,106,191,0.4)"
            strokeWidth="0.15"
            rx="0.4"
          />
          {/* median marker */}
          <line
            x1={medianP}
            y1="9"
            x2={medianP}
            y2="19"
            stroke="#0a6abf"
            strokeWidth="0.4"
          />
          {/* your max CPC marker */}
          <line
            x1={yoursP}
            y1="7"
            x2={yoursP}
            y2="21"
            stroke={yoursColor}
            strokeWidth="0.5"
          />
          <circle cx={yoursP} cy="14" r="0.9" fill={yoursColor} />
        </svg>
        {/* Scale labels */}
        <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-ink-4">
          <span>0,00 €</span>
          <span>{scaleMax.toFixed(2)} €</span>
        </div>

        {/* Legend */}
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-ink-2">
          <Legend
            color="rgba(10,106,191,0.4)"
            label={`${c.visualLegendRange}: ${ind.minCPC.toFixed(2)}–${ind.maxCPC.toFixed(2)} €`}
            block
          />
          <Legend
            color="#0a6abf"
            label={`${c.visualLegendMedian}: ${ind.medianCPC.toFixed(2)} €`}
          />
          <Legend
            color={yoursColor}
            label={`${c.visualLegendYours}: ${results.maxCPC.toFixed(2)} €`}
          />
        </div>

        <p className="mt-5 text-sm leading-relaxed text-ink-3">
          {locale === "de" ? ind.notesDe : ind.notesEn}
        </p>
      </div>
    </div>
  );
}

function Legend({
  color,
  label,
  block,
}: {
  color: string;
  label: string;
  block?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      {block ? (
        <span
          aria-hidden
          className="inline-block h-3 w-5 rounded-sm"
          style={{ backgroundColor: color }}
        />
      ) : (
        <span
          aria-hidden
          className="inline-block h-3 w-0.5"
          style={{ backgroundColor: color }}
        />
      )}
      <span>{label}</span>
    </span>
  );
}

function VerdictReasoning({
  locale,
  c,
  results,
}: {
  locale: Locale;
  c: CopyT;
  results: ViabilityResults;
}) {
  const text = buildReasoning(locale, results);
  return (
    <div>
      <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
        {c.reasoningTitle}
      </h3>
      <div className="space-y-4 rounded-lg border border-rule bg-bg-subtle p-6 md:p-8">
        {text.split("\n\n").map((para, i) => (
          <p key={i} className="text-[15px] leading-relaxed text-ink-2">
            {para}
          </p>
        ))}
      </div>
    </div>
  );
}

function buildReasoning(locale: Locale, r: ViabilityResults): string {
  const ind = r.industry;
  const indLabel = locale === "de" ? ind.labelDe : ind.labelEn;
  const indNotes = locale === "de" ? ind.notesDe : ind.notesEn;
  const median = ind.medianCPC.toFixed(2);
  const cpc = r.maxCPC.toFixed(2);
  const cpa = r.maxCPA.toFixed(2);
  const gapPct = r.cpcGapToMedianPct.toFixed(1);
  const absGapPct = Math.abs(r.cpcGapToMedianPct).toFixed(1);

  if (r.verdict === "solid") {
    return locale === "de"
      ? `Ihre wirtschaftlichen Parameter erlauben einen maximalen CPC von ${cpc} € — das liegt ${gapPct}% über dem Branchen-Median von ${median} € für ${indLabel}. Performance-Marketing ist in Ihrer Konstellation wirtschaftlich tragfähig.\n\nDie Frage ist nicht „ob", sondern „wie effizient". Die Calvarius-Methodik setzt auf drei Hebel: präzise Account-Struktur mit klarer Kampagnen-Differenzierung, kontinuierliche Conversion-Rate-Optimierung durch strukturierte Landingpage-Tests und sauberes Negative-Keyword-Management. In typischen Calvarius-Mandaten reduzieren wir den effektiven CPC bei gleichem Volumen um 15-25% unter den Branchen-Median — Ihr wirtschaftlicher Puffer wächst entsprechend.\n\nMaximal-tragfähiger CPA: ${cpa} €.\n\n${indNotes}`
      : `Your economic parameters allow a maximum CPC of €${cpc} — that is ${gapPct}% above the industry median of €${median} for ${indLabel}. Performance marketing is economically viable in your configuration.\n\nThe question is not 'whether' but 'how efficiently'. Calvarius methodology focuses on three levers: precise account structure with clear campaign differentiation, continuous conversion rate optimization through structured landing page tests, and clean negative keyword management. In typical Calvarius mandates, we reduce the effective CPC by 15-25% below industry median at the same volume — your economic buffer grows accordingly.\n\nMaximum sustainable CPA: €${cpa}.\n\n${indNotes}`;
  }
  if (r.verdict === "tight") {
    return locale === "de"
      ? `Ihre wirtschaftlichen Parameter erlauben einen maximalen CPC von ${cpc} € — das liegt im Bereich des Branchen-Medians von ${median} € für ${indLabel}. Performance-Marketing ist in Ihrer Konstellation wirtschaftlich machbar, aber knapp.\n\nBei knapper Wirtschaftlichkeit entscheidet methodische Disziplin über Profitabilität. Calvarius-Ansatz: präzises Targeting reduziert Streuverluste systematisch, die Fokussierung auf konversionsstarke Keywords statt Volumen-Suchen verschiebt das Effizienz-Profil substantiell, und Negative-Keyword-Management räumt non-performende Suchanfragen konsequent aus dem Account. Hinzu kommt methodische Conversion-Rate-Optimierung: Steigt die CVR von 3% auf 5%, verdoppelt sich die wirtschaftliche Marge.\n\nBei knapper Wirtschaftlichkeit ist die Wahl der Agentur kritisch. Methodisch arbeitende Performance-Marketing-Partner unterscheiden sich substantiell von Standard-Anbietern — wir messen den Unterschied in mandate-typischer CPC-Reduktion von 20-30% unter Branchen-Median.\n\nMaximal-tragfähiger CPA: ${cpa} €.\n\n${indNotes}`
      : `Your economic parameters allow a maximum CPC of €${cpc} — that is in the range of the industry median of €${median} for ${indLabel}. Performance marketing is economically feasible in your configuration, but tight.\n\nIn tight viability, methodical discipline determines profitability. Calvarius approach: precise targeting systematically reduces waste, focusing on conversion-strong keywords instead of volume searches substantially shifts the efficiency profile, and negative keyword management consistently clears non-performing search queries from the account. Plus methodical conversion rate optimization: when CVR increases from 3% to 5%, the economic margin doubles.\n\nIn tight viability, the choice of agency is critical. Methodical performance marketing partners differ substantially from standard providers — we measure the difference in mandate-typical CPC reduction of 20-30% below industry median.\n\nMaximum sustainable CPA: €${cpa}.\n\n${indNotes}`;
  }
  // problematic
  return locale === "de"
    ? `Ihre wirtschaftlichen Parameter erlauben einen maximalen CPC von ${cpc} € — das liegt ${absGapPct}% unter dem Branchen-Median von ${median} € für ${indLabel}. Mit Standard-Performance-Marketing-Strategien wäre das wirtschaftlich problematisch.\n\nStandard-Ansätze reichen hier nicht aus. Methodische Optimierung kann die wirtschaftliche Logik allerdings substantiell verschieben. Vier Hebel sind möglich:\n\nErstens, Conversion-Rate-Optimierung. Verdoppelt sich die CVR, halbiert sich der erforderliche effektive CPC bei gleicher Wirtschaftlichkeit. CRO ist oft günstiger als zusätzliches Ad-Budget — und in vielen Calvarius-Mandaten der substantielle Hebel.\n\nZweitens, präzises Targeting und Streuverlust-Reduktion. Statt breite Reichweite einzukaufen, fokussiert Calvarius auf konversionsstarke Suchanfragen und Zielgruppen. Negative-Keyword-Management räumt den Account kontinuierlich auf. Das verschiebt den effektiven CPC oft 20-30% unter Branchen-Median.\n\nDrittens, AOV-Steigerung durch Cross-Selling, Up-Selling oder Bundle-Strategien. Steigt der durchschnittliche Auftragswert um 30-50%, ändert sich die wirtschaftliche Logik fundamental.\n\nViertens, Branchen-Einordnung prüfen. Wenn der AOV für die gewählte Branche untypisch ist, ist die Klassifizierung möglicherweise das eigentliche Problem — eine andere Branche mit niedrigerem Median-CPC könnte methodisch passender sein.\n\nMaximal-tragfähiger CPA: ${cpa} €.\n\n${indNotes}`
    : `Your economic parameters allow a maximum CPC of €${cpc} — that is ${absGapPct}% below the industry median of €${median} for ${indLabel}. With standard performance marketing strategies, this would be economically problematic.\n\nStandard approaches are not sufficient here. But methodical optimization can substantially shift the economic logic. Four levers are possible:\n\nFirst, conversion rate optimization. When CVR doubles, the effective CPC requirement halves at the same economic viability. CRO is often cheaper than additional ad budget — and in many Calvarius mandates, the substantial lever.\n\nSecond, precise targeting and waste reduction. Instead of buying broad reach, Calvarius focuses on conversion-strong search queries and target groups. Negative keyword management continuously cleans up the account. This shifts the effective CPC often 20-30% below industry median.\n\nThird, AOV increase through cross-selling, up-selling, or bundle strategies. When the average order value rises by 30-50%, the economic logic fundamentally changes.\n\nFourth, check the industry classification. When AOV is atypical for the selected industry, the classification may be the actual problem — a different industry with lower median CPC could be methodically more fitting.\n\nMaximum sustainable CPA: €${cpa}.\n\n${indNotes}`;
}

function EmailReportCTA({
  locale,
  c,
  results,
  usageId,
  reportFn,
  inputs,
}: {
  locale: Locale;
  c: CopyT;
  results: ViabilityResults;
  usageId: number | null;
  reportFn: ReturnType<typeof useServerFn<typeof requestViabilityReport>>;
  inputs: ViabilityInputs;
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">(
    "idle",
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.includes("@")) return;
    setState("sending");
    try {
      await reportFn({
        data: {
          email,
          locale,
          payload: {

            aov: inputs.aov,
            grossMargin: inputs.grossMargin,
            marketingMargin: inputs.marketingMargin,
            conversionRate: inputs.conversionRate,
            industrySlug: inputs.industrySlug,
            industryLabel:
              locale === "de"
                ? results.industry.labelDe
                : results.industry.labelEn,
            maxCPA: results.maxCPA,
            maxCPC: results.maxCPC,
            industryMedianCPC: results.industry.medianCPC,
            verdict: results.verdict,
            cpcGapToMedianPct: results.cpcGapToMedianPct,
          },
        },
      });
      setState("done");
    } catch (err) {
      console.error(err);
      setState("error");
    }
  }

  return (
    <div className="rounded-lg border border-rule bg-white p-6 md:p-8">
      <h3 className="font-display text-xl text-ink md:text-2xl">
        {c.cta1Headline}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed text-ink-3">{c.cta1Sub}</p>
      {state === "done" ? (
        <div className="mt-5 rounded-md border border-lime/40 bg-lime-tint px-4 py-3 text-sm text-ink-2">
          {c.cta1Done}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-5 flex flex-wrap gap-3">
          <input
            type="email"
            required
            placeholder={c.cta1Email}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-w-0 flex-1 rounded-md border border-rule bg-white px-4 py-3 text-ink outline-none transition focus:border-cyan-deep focus:ring-2 focus:ring-cyan-deep/20"
          />
          <button
            type="submit"
            disabled={state === "sending"}
            className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90 disabled:opacity-60"
          >
            {state === "sending" ? c.cta1Sending : `${c.cta1Submit} →`}
          </button>
          {state === "error" && (
            <p
              role="alert"
              className="basis-full text-sm text-destructive"
            >
              {c.cta1Error}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

function StrategyMeetingCTA({
  locale,
  c,
  results,
  inputs,
}: {
  locale: Locale;
  c: CopyT;
  results: ViabilityResults;
  inputs: ViabilityInputs;
}) {
  const subjectKey = locale === "de" ? "betreff" : "subject";
  const params = new URLSearchParams();
  params.set(subjectKey, c.subjectParam);
  // Topic prefill — map to existing form topic slug
  params.set("topic", "performance-marketing");
  params.set("aov", String(inputs.aov));
  params.set("grossMargin", String(inputs.grossMargin));
  params.set("marketingMargin", String(inputs.marketingMargin));
  params.set("conversionRate", String(inputs.conversionRate));
  params.set("industry", inputs.industrySlug);
  params.set("maxCPC", results.maxCPC.toFixed(2));
  params.set("maxCPA", results.maxCPA.toFixed(2));
  params.set("verdict", results.verdict);

  const href = `${c.contactTo}?${params.toString()}`;

  return (
    <div className="rounded-lg border border-cyan/30 bg-bg-subtle p-6 md:p-8">
      <h3 className="font-display text-xl text-ink md:text-2xl">
        {c.cta2Headline}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed text-ink-3">{c.cta2Sub}</p>
      <a
        href={href}
        className="mt-5 inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90"
      >
        {c.cta2Button} →
      </a>
    </div>
  );
}

function DirectMandateCTA({ c }: { c: CopyT }) {
  return (
    <div className="rounded-lg border border-rule bg-white p-6 md:p-8">
      <h3 className="font-display text-xl text-ink md:text-2xl">
        {c.cta3Headline}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed text-ink-3">{c.cta3Sub}</p>
      <Link
        to={c.contactTo}
        className="mt-5 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider text-cyan-deep hover:underline"
      >
        {c.cta3Button} →
      </Link>
    </div>
  );
}
